import {
  BasicButton,
  ButtonGroup,
  Grid,
  GridItem,
  LegacyModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
  PrimaryButton,
  DateSelectField,
  TextInputField,
  CheckboxField,
  Toggle,
  Text,
  Link,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { date, object, string } from "yup";

import { InstitutionsSelect } from "./InstitutionsField";
import { Education } from "./type";
import {
  CountrySelect,
  ProvinceSelect,
} from "../../../../components/AddressInput";
import { customFieldLabels } from "../../../../utils/address-utils";

type EducationModalProps = {
  index: number;
  education?: Education;
  onSave: (education: Education) => void;
  trigger: JSX.Element;
  onDelete?: () => void;
  allowHighest: boolean;
};
export function EducationModal({
  education,
  onSave,
  index,
  trigger,
  onDelete,
  allowHighest,
}: EducationModalProps) {
  const { t } = useTranslation();
  const [isCurrentEducation, setIsCurrentEducation] = useState<boolean>(
    !!education?.current
  );

  const schema = object().shape({
    address: object().shape({
      country: string().trim().required().label(t("Country")),
    }),

    institution: object().shape({
      name: string().trim().required().label(t("Institution")),
    }),
    start_date: string().required().label(t("Start date")),
    end_date: date().when(["start_date"], ([start_date], schema) => {
      if (isCurrentEducation) {
        return schema.notRequired();
      }

      if (start_date) {
        return schema
          .required()
          .label(t("End date"))
          .min(start_date, t("End date must be after start date"));
      }

      return schema.required().label(t("End date"));
    }),
    degree: string().required().label(t("Degree title")),
  });

  return (
    <ModalTrigger trigger={() => trigger}>
      {(close) => (
        <Formik
          initialValues={{
            address: education?.address || {
              province_state: "",
              country: "",
            },
            highest_level: education?.highest_level || false,
            degree: education?.degree || "",
            institution: {
              name: education?.institution || "",
              external_id: education?.institution_external_id || "",
              province_state: null,
            },
            start_date: education?.start_date || "",
            end_date: education?.end_date || null,
            current: education?.current || false,
            index,
          }}
          onSubmit={(education) => {
            onSave({
              ...education,
              institution: education.institution.name,
              institution_external_id: education.institution.external_id,
              end_date: isCurrentEducation ? null : education.end_date,
            });
            close();
          }}
          validationSchema={schema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ values, setFieldValue }) => {
            const labels =
              customFieldLabels[values.address.country] ?? customFieldLabels.CA;
            return (
              <Form>
                <LegacyModal width="456px">
                  <ModalHeader
                    close={close}
                    title={education ? t("Edit education") : t("Add education")}
                  />
                  <ModalContent>
                    <Grid>
                      <GridItem>
                        <Text style="form/section-heading">
                          <Trans>Education details</Trans>
                        </Text>
                      </GridItem>
                      <GridItem>
                        <Field
                          name="address.country"
                          label={t("Country")}
                          as={CountrySelect}
                          isRequired
                          testID="add-education-modal-country-select"
                        />
                      </GridItem>
                      <GridItem>
                        <ProvinceSelect
                          name="address.province_state"
                          testID="add-education-modal-province-select"
                          label={labels.province_state}
                          country={values.address.country}
                        />
                      </GridItem>
                      {["US", "CA"].includes(values.address.country) &&
                      values.address.province_state ? (
                        <GridItem>
                          <Field
                            name="institution"
                            label={t("Institution")}
                            as={InstitutionsSelect}
                            country={values.address.country}
                            province_state={values.address.province_state}
                            isRequired
                            testID="add-education-modal-usa-and-can-institution-input"
                          />
                        </GridItem>
                      ) : (
                        <GridItem>
                          <Field
                            name="institution.name"
                            label={t("Institution")}
                            as={TextInputField}
                            isRequired
                            testID="add-education-modal-institution-input"
                          />
                        </GridItem>
                      )}
                      <GridItem>
                        <Field
                          name="degree"
                          label={t("Degree title")}
                          as={TextInputField}
                          isRequired
                          description={t("E.g. Bachelor of Arts")}
                          testID="add-education-modal-degree-title-input"
                        />
                      </GridItem>
                      {allowHighest && (
                        <GridItem>
                          <Field
                            name="highest_level"
                            as={CheckboxField}
                            testID="add-education-modal-highest-level-checkbox"
                          >
                            <Trans>This is my highest level of education</Trans>
                          </Field>
                        </GridItem>
                      )}
                      <GridItem>
                        <Field
                          label={t("Start date")}
                          name="start_date"
                          isRequired
                          fullWidth
                          isApproximateDate
                          as={DateSelectField}
                          testID="add-education-modal-start-date-select"
                        />
                      </GridItem>

                      <GridItem
                        alignItems="left"
                        className={css({
                          flexDirection: "row",
                        })}
                      >
                        <Toggle
                          isSelected={isCurrentEducation}
                          onChange={(value) => {
                            setFieldValue("end_date", null);
                            setFieldValue("current", value);
                            setIsCurrentEducation(value);
                          }}
                          testID="add-education-modal-currently-attend"
                        >
                          {t("I currently attend this institution")}
                        </Toggle>
                      </GridItem>
                      {!isCurrentEducation && (
                        <GridItem>
                          <Field
                            label={t("Date awarded")}
                            name="end_date"
                            isRequired
                            fullWidth
                            isApproximateDate
                            as={DateSelectField}
                            testID="add-education-modal-date-awarded-select"
                          />
                        </GridItem>
                      )}
                    </Grid>
                  </ModalContent>
                  <ModalFooter
                    className={css({
                      justifyContent: onDelete ? "space-between" : "flex-end",
                    })}
                  >
                    {onDelete && (
                      <Link
                        textStyle="global/body-secondary"
                        textColor="text-weak"
                        onPress={() => {
                          onDelete();
                          close();
                        }}
                        testID="add-education-modal-delete-education-button"
                      >
                        <Trans>Delete education</Trans>
                      </Link>
                    )}
                    <ButtonGroup>
                      <BasicButton
                        size="medium"
                        onPress={close}
                        testID="add-education-modal-cancel-button"
                      >
                        <Trans>Cancel</Trans>
                      </BasicButton>
                      <PrimaryButton
                        type="submit"
                        size="medium"
                        testID="add-education-modal-save-button"
                      >
                        <Trans>Save education</Trans>
                      </PrimaryButton>
                    </ButtonGroup>
                  </ModalFooter>
                </LegacyModal>
              </Form>
            );
          }}
        </Formik>
      )}
    </ModalTrigger>
  );
}
